// Time + timezone helpers for 12-hour display and per-event timezones.

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

// Curated list of common time zones with display label + short abbreviation.
export const TIMEZONES = [
  { iana: 'America/Los_Angeles', label: 'Pacific Time (PT)', abbr: 'PT' },
  { iana: 'America/Denver', label: 'Mountain Time (MT)', abbr: 'MT' },
  { iana: 'America/Phoenix', label: 'Arizona (MST)', abbr: 'MST' },
  { iana: 'America/Chicago', label: 'Central Time (CT)', abbr: 'CT' },
  { iana: 'America/New_York', label: 'Eastern Time (ET)', abbr: 'ET' },
  { iana: 'America/Anchorage', label: 'Alaska Time (AKT)', abbr: 'AKT' },
  { iana: 'Pacific/Honolulu', label: 'Hawaii Time (HT)', abbr: 'HT' },
  { iana: 'America/Toronto', label: 'Toronto (ET)', abbr: 'ET' },
  { iana: 'America/Mexico_City', label: 'Mexico City (CT)', abbr: 'CT' },
  { iana: 'UTC', label: 'UTC', abbr: 'UTC' },
  { iana: 'Europe/London', label: 'London (UK)', abbr: 'UK' },
  { iana: 'Europe/Paris', label: 'Central Europe (CET)', abbr: 'CET' },
  { iana: 'Australia/Sydney', label: 'Sydney (AET)', abbr: 'AET' },
];

const ABBR_BY_IANA = TIMEZONES.reduce((acc, z) => { acc[z.iana] = z.abbr; return acc; }, {});

export function zoneAbbr(iana) {
  if (!iana) return ABBR_BY_IANA[DEFAULT_TIMEZONE];
  return ABBR_BY_IANA[iana] || iana.split('/').pop().replace(/_/g, ' ');
}

export function zoneLabel(iana) {
  const z = TIMEZONES.find((t) => t.iana === iana);
  return z ? z.label : (iana || '');
}

// '18:30' -> '6:30 PM'  (empty/invalid -> '')
export function formatTime12(hhmm) {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return '';
  const [hStr, mStr] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, '0')} ${period}`;
}

// Build "6:00 PM – 9:00 PM PT" from an event object (or just "6:00 PM PT").
export function formatEventTime(event) {
  if (!event) return '';
  const start = formatTime12(event.time);
  if (!start) return '';
  const end = formatTime12(event.end_time);
  const abbr = zoneAbbr(event.timezone);
  const range = end ? `${start} – ${end}` : start;
  return abbr ? `${range} ${abbr}` : range;
}

// 'YYYY-MM-DD' -> 'Sat, Jun 7, 2026' (kept tz-neutral to avoid off-by-one)
export function formatDateLong(dateStr) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr || '';
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  });
}
